import React, { useState, useEffect } from 'react';
import { Button, Form, Input, TextArea, Message } from 'semantic-ui-react';
import Joi from 'joi';
import useAxios from 'axios-hooks';
import ModalForm from '../ModalForm';
import ChangeReservationType from './ChangeReservationType';
import RoomSelectField from '../Fields/RoomSelectField';
import DatepickerField from '../Fields/DatepickerField';
import TimepickerField from '../Fields/TimepickerField';
import validateSchema from '../../utils/validateSchema';
import SchemaErrors from '../SchemaErrors';
import DateUtils, { FORMAT } from '../../utils/dateUtils';
import moment from '../../utils/moment';
import { RESERVATION_TYPES } from '../../utils/constants';
import { ownerSelector } from '../../selectors';
import useAvailableDates from '../../hooks/useAvailableDates';

const today = moment();

const reservationSchema = Joi.object({
  guestFirstName: Joi.string().required(),
  guestLastName: Joi.string().required(),
  guestEmail: Joi.string()
    .email({ minDomainSegments: 2, tlds: { allow: ['com', 'net'] } })
    .required(),
  guestPhone: Joi.string().required(),
  checkIn: Joi.date().required(),
  checkOut: Joi.date().greater(Joi.ref('checkIn')).required(),
  arrivalTime: Joi.string().allow(null, ''),
  roomId: Joi.number().required(),
  notes: Joi.string().allow(null, '')
});

const initialState = {
  failed: false,
  errors: {},
  data: {
    guestFirstName: '',
    guestLastName: '',
    guestEmail: '',
    guestPhone: '',
    checkIn: '',
    checkOut: '',
    arrivalTime: '',
    roomId: null,
    notes: '',
    type: RESERVATION_TYPES.GUEST_OF_OWNER
  }
};

const GuestOfOwnerReservationForm = ({ onChangeType, onClose, onSuccess }) => {
  const { roomInfoList } = ownerSelector();
  const onlyOneRoom = roomInfoList?.length === 1;
  const [state, setState] = useState(initialState);
  const [{ loading }, execute] = useAxios(
    {
      url: '/api/createReservation',
      method: 'POST'
    },
    { manual: true }
  );

  const [currentDate, setCurrentDate] = useState(today);
  const currentYear = moment(currentDate).format('YYYY');

  const { loadingAvailableDates, availableDates } = useAvailableDates({
    shouldExecute: onlyOneRoom,
    startDate: moment(currentDate).startOf('year').format(FORMAT),
    endDate: moment(currentDate).endOf('year').format(FORMAT),
    roomId: roomInfoList[0]?.id
  });

  const { failed, errors, data } = state;
  const {
    type,
    guestFirstName,
    guestLastName,
    guestEmail,
    guestPhone,
    checkIn,
    checkOut,
    arrivalTime,
    roomId,
    notes
  } = data;

  useEffect(() => {
    if (onlyOneRoom) {
      setState({
        ...state,
        data: { ...data, roomId: roomInfoList[0]?.id }
      });
    }
  }, []);

  const onError = error => {
    console.error(error);
    setState({ ...state, failed: true });
  };

  const validateDates = () => {
    const promise = new Promise((resolve, reject) => {
      if (onlyOneRoom) {
        const dates = DateUtils.getDaysInBetween(checkIn, checkOut);
        dates.forEach(date => {
          if (!availableDates[date]) {
            reject(new Error('All the dates in between must be available'));
          }
        });
      }

      resolve();
    });

    return promise;
  };

  const onSubmit = () => {
    validateDates()
      .then(() => {
        validateSchema(reservationSchema, data)
          .then(() => {
            let at = '';
            const parsedArrivalTime = DateUtils.parseTime(arrivalTime);
            if (parsedArrivalTime.toLowerCase() !== 'invalid date') {
              at = parsedArrivalTime;
            }
            execute({
              data: {
                ...data,
                arrivalTime: at
              }
            })
              .then(onSuccess)
              .catch(onError);
          })
          .catch(schemaErrors => {
            setState({ ...state, errors: schemaErrors });
          });
      })
      .catch(({ message }) => {
        setState({ ...state, errors: { dates: message } });
      });
  };

  const onChange = (_event, { id, value }) => {
    setState({
      ...state,
      data: { ...data, [id]: value },
      errors: { ...errors, [id]: null }
    });
  };

  const filterDate = date => {
    if (onlyOneRoom) {
      if (DateUtils.isAnotherYear(date, currentYear)) {
        setCurrentDate(date);
        return false;
      }

      return availableDates[DateUtils.defaultFormat(date)];
    }

    return true;
  };

  return (
    <Form loading={loading} onSubmit={onSubmit} onClose={onClose}>
      <ModalForm.Fields>
        <ChangeReservationType type={type} onChange={onChangeType} />
        <Form.Group widths="equal">
          <Form.Field
            id="guestFirstName"
            control={Input}
            label="Guest First Name"
            placeholder="Guest First Name"
            type="string"
            value={guestFirstName}
            onChange={onChange}
            error={errors.guestFirstName}
          />
          <Form.Field
            id="guestLastName"
            control={Input}
            label="Guest Last Name"
            placeholder="Guest Last Name"
            type="string"
            value={guestLastName}
            onChange={onChange}
            error={errors.guestLastName}
          />
        </Form.Group>
        <Form.Group widths="equal">
          <Form.Field
            id="guestEmail"
            control={Input}
            label="Guest Email Address"
            placeholder="Guest Email Address"
            type="string"
            value={guestEmail}
            onChange={onChange}
            error={errors.guestEmail}
          />
          <Form.Field
            id="guestPhone"
            control={Input}
            label="Guest Phone Number"
            placeholder="Guest Phone Number"
            type="number"
            value={guestPhone}
            onChange={onChange}
            error={errors.guestPhone}
          />
        </Form.Group>
        <Form.Group widths="equal">
          <DatepickerField
            id="checkIn"
            minDate={DateUtils.toDate(new Date())}
            label="Arrival Date"
            placeholder="Select Date"
            value={checkIn}
            onChange={onChange}
            error={errors.checkIn}
            filterDate={filterDate}
            disabled={loadingAvailableDates}
          />
          <DatepickerField
            id="checkOut"
            minDate={DateUtils.toDate(DateUtils.addDay(checkIn))}
            label="Departure Date"
            placeholder="Select Date"
            disabled={loadingAvailableDates || !checkIn}
            value={checkOut}
            onChange={onChange}
            error={errors.checkOut}
            filterDate={filterDate}
          />
        </Form.Group>
        <TimepickerField
          id="arrivalTime"
          label="Estimated Arrival Time"
          placeholder="Estimated Arrival Time"
          value={arrivalTime}
          onChange={onChange}
          error={errors.arrivalTime}
        />
        <RoomSelectField
          id="roomId"
          value={roomId}
          onChange={onChange}
          checkIn={checkIn}
          checkOut={checkOut}
          error={errors.roomId}
          onlyOneRoom={onlyOneRoom}
        />
        <Form.Field
          id="notes"
          control={TextArea}
          rows={5}
          label="Notes"
          placeholder="Notes"
          type="text"
          value={notes}
          onChange={onChange}
          error={errors.notes}
        />
        <SchemaErrors errors={errors} />
        <Message
          header="Something Went Wrong"
          content="Please, try again later"
          visible={!!failed}
          error
        />
      </ModalForm.Fields>
      <ModalForm.Actions>
        <Button color="red" disabled={loading} onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading} primary>
          Save Reservation
        </Button>
      </ModalForm.Actions>
    </Form>
  );
};

export default React.memo(GuestOfOwnerReservationForm);
